import React, { useMemo, useContext } from 'react';
import { ResultsContext } from '../contexts/ResultsContext';
import './PlayerComparison.css';

/**
 * PlayerComparison component displays a side-by-side comparison of two players
 * 
 * @param {Object} props - Component props
 * @param {string} props.player1 - First player's username
 * @param {string} props.player2 - Second player's username
 * @param {Array} props.games - Array of games in the tournament
 * @param {string} props.theme - Current theme (dark/light)
 * @param {Function} props.onClose - Function to close the comparison view
 */
const PlayerComparison = ({ player1, player2, games, theme, onClose }) => {
  const { results } = useContext(ResultsContext);
  // Process games data for comparison
  const comparisonData = useMemo(() => {
    if (!games) return [];

    return games.map(game => {
      const p1Entry = game.leaderboard?.find(entry => 
        entry.userName.toLowerCase() === player1.toLowerCase()
      );
      const p2Entry = game.leaderboard?.find(entry => 
        entry.userName.toLowerCase() === player2.toLowerCase()
      );

      const p1Score = p1Entry ? p1Entry.score : null;
      const p2Score = p2Entry ? p2Entry.score : null;

      // Determine who has the better score (lower rank/higher score)
      let p1Wins = false;
      let p2Wins = false;

      if (p1Score !== null && p2Score !== null) {
        if (p1Score > p2Score) p1Wins = true;
        else if (p2Score > p1Score) p2Wins = true;
      } else if (p1Score !== null) {
        p1Wins = true;
      } else if (p2Score !== null) {
        p2Wins = true;
      }

      return {
        id: game.id || game.title,
        title: game.title,
        p1: {
          rank: p1Entry ? p1Entry.rank : null,
          score: p1Score,
          wins: p1Wins
        },
        p2: {
          rank: p2Entry ? p2Entry.rank : null,
          score: p2Score,
          wins: p2Wins
        }
      };
    });
  }, [games, player1, player2]);

  return (
    <div className={`player-comparison-container ${theme}`}>
      <div className="comparison-header">
        <div className="comparison-players-info">
          <div className="comp-player-info">
            {results?.users?.[player1]?.avatar_path && (
              <div className="comp-avatar-circle" style={{ backgroundColor: results.users[player1].background_color_hex }}>
                <img 
                  src={results.users[player1].avatar_path} 
                  alt={player1} 
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            <span style={{ color: results?.users?.[player1]?.background_color_hex }}>{player1.toUpperCase()}</span>
          </div>
          <span className="vs-text">VS</span>
          <div className="comp-player-info">
            <span style={{ color: results?.users?.[player2]?.background_color_hex }}>{player2.toUpperCase()}</span>
            {results?.users?.[player2]?.avatar_path && (
              <div className="comp-avatar-circle" style={{ backgroundColor: results.users[player2].background_color_hex }}>
                <img 
                  src={results.users[player2].avatar_path} 
                  alt={player2} 
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
          </div>
        </div>
        <button className="close-comparison-btn" onClick={onClose}>
          CLOSE COMPARISON
        </button>
      </div>

      <div className="comparison-table-wrapper">
        <table className="comparison-table">
          <thead>
            <tr>
              <th className="col-table">TABLE</th>
              <th className="col-player">{player1.toUpperCase()}</th>
              <th className="col-player">{player2.toUpperCase()}</th>
            </tr>
          </thead>
          <tbody>
            {comparisonData.map((game) => (
              <tr key={game.id}>
                <td className="comp-game-title">{game.title}</td>
                <td className={`player-stats ${game.p1.wins ? 'winner' : ''}`}>
                  {game.p1.score !== null ? (
                    <>
                      <span className="rank">#{game.p1.rank}</span>
                      <span className="score">{game.p1.score.toLocaleString()}</span>
                      {game.p1.wins && <span className="star">★</span>}
                    </>
                  ) : (
                    <span className="no-score">No Score</span>
                  )}
                </td>
                <td className={`player-stats ${game.p2.wins ? 'winner' : ''}`}>
                  {game.p2.score !== null ? (
                    <>
                      <span className="rank">#{game.p2.rank}</span>
                      <span className="score">{game.p2.score.toLocaleString()}</span>
                      {game.p2.wins && <span className="star">★</span>}
                    </>
                  ) : (
                    <span className="no-score">No Score</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlayerComparison;
